import { CREATURE_BY_ID, creatureLook } from "@eldrun/content";
import { Box3, Mesh, PerspectiveCamera, PlaneGeometry, Scene, Vector3, type SkinnedMesh, type WebGLRenderer } from "three";
import { CREATURE_MODELS } from "../assets/creatures.generated.ts";
import { FrameTimer } from "../engine/frame-loop.ts";
import { FrameStats } from "../engine/frame-stats.ts";
import { StatsHud } from "../engine/stats-hud.ts";
import { applyCreaturePose, CreatureAnimator } from "../game/creature-animation.ts";
import { createCreatureModel } from "../game/creature-model.ts";
import { CreatureSkins, isAnimatedType, type SkinnedCreature } from "../game/creature-skins.ts";
import { applyAtmosphere, createAtmosphereUniforms, sampleAtmosphere } from "../render/atmosphere.ts";
import { PostFx } from "../render/post/post-fx.ts";
import { countPrograms } from "../render/post/post-model.ts";
import { createSurfaceMaterial } from "../render/shaders/surface.ts";

export interface CreatureShowcaseOptions {
  /** Um tipo animado, alguns (`modelo=lobisomem,corpo-seco`, na ordem do módulo gerado) ou todos (`undefined`). */
  type: string | undefined;
  /** Clipe do contrato (`parado`, `andando`, `golpe:garra`), ou `golpe` (o primeiro golpe de cada tipo). */
  clip: string;
  /** Instante fixo do clipe (s); sem ele, o clipe repete. */
  time: number | undefined;
  /** Folha de quadros (`&instantes=0,0.3,0.6`): a primeira variante de cada tipo, uma vez por instante. */
  times?: readonly number[];
  /** De onde a câmera olha; `diagonal` (de frente, 45° à direita) mostra a passada numa folha de quadros. */
  view: "frente" | "lado" | "costas" | "diagonal";
  phase: number;
  /** O boneco procedural ao lado de cada modelo (as cores da troca). */
  procedural: boolean;
  /** Reforçada (rim 3×). */
  elite: boolean;
}

export interface CreatureShowcase {
  stats(): Record<string, unknown>;
  stop(): void;
}

const GAP = 0.8;

/**
 * Vitrine dos modelos animados das criaturas (`?debug=criaturas&modelo=todos|<id>&clipe=&instante=&vista=&boneco=0&reforcada=1`,
 * F50, sem servidor): cada criatura animada, cada variante, num clipe do contrato parado num instante
 * ou em laço, com o boneco procedural ao lado (as cores que a troca mostra). Para conferir escala,
 * pesos, peças presas, cores e o quadro do impacto sem abrir uma partida.
 */
export async function startCreatureShowcase(renderer: WebGLRenderer, options: CreatureShowcaseOptions): Promise<CreatureShowcase> {
  const scene = new Scene();
  const atmosphere = createAtmosphereUniforms();
  const sample = sampleAtmosphere(options.phase);
  scene.background = sample.horizon;
  const camera = new PerspectiveCamera(45, 1, 0.1, 200);
  const ground = new Mesh(new PlaneGeometry(80, 80), createSurfaceMaterial({ atmosphere, name: "chao", color: "#7FA34E", blobs: true, normal: "up" }));
  ground.rotation.x = -Math.PI / 2;
  scene.add(ground);
  const skins = new CreatureSkins(atmosphere);
  const types = (Object.keys(CREATURE_MODELS) as (keyof typeof CREATURE_MODELS)[]).filter((t) => options.type === undefined || options.type.split(",").includes(t));
  await Promise.all(types.map((t) => skins.request(t)));
  const lineup: { type: string; variant: string; clip: string; body: SkinnedCreature; width: number; time?: number }[] = [];
  const dolls: { animator: CreatureAnimator; rig: ReturnType<typeof createCreatureModel> }[] = [];
  let x = 0;
  // O comprimento do maior corpo, parado (o Dragão tem ~11 m do focinho à cauda e abre ~15 m de asas): a distância da câmera.
  let deepest = 0;
  for (const type of types) {
    if (!isAnimatedType(type) || !skins.ready(type)) continue;
    const look = creatureLook(type)!;
    const def = CREATURE_BY_ID.get(type)!;
    const table = CREATURE_MODELS[type].clips as Readonly<Record<string, unknown>>;
    const clip = options.clip === "golpe" ? (Object.keys(table).find((n) => n.startsWith("golpe:")) ?? "parado") : options.clip in table ? options.clip : "parado";
    const frames = options.times?.map((time) => ({ variant: look.variants[0]!.id, time })) ?? look.variants.map((v) => ({ variant: v.id, time: undefined }));
    const bodies = frames.map((frame) => skins.acquire(type, frame.variant, { elite: options.elite, night: def.archetype === "assombracao" }, def.haunt?.fly !== undefined)!);
    // Largura de cada um: o raio do corpo no servidor ou a do corpo parado (asas, braços), com folga.
    const size = new Box3().setFromObject(bodies[0]!.root, true).getSize(new Vector3());
    deepest = Math.max(deepest, size.z);
    const width = Math.max(1.2, def.radius * 4, look.height * 0.9, size.x * 1.1);
    for (const [i, frame] of frames.entries()) {
      const body = bodies[i]!;
      body.root.position.set(x + width / 2, 0, 0);
      scene.add(body.root);
      lineup.push({ type, variant: frame.variant, clip, body, width, ...(frame.time !== undefined && { time: frame.time }) });
      x += width;
    }
    if (options.procedural) {
      const rig = createCreatureModel(atmosphere, type, options.elite);
      rig.root.position.set(x + width / 2, 0, 0);
      scene.add(rig.root);
      dolls.push({ animator: new CreatureAnimator(), rig });
      x += width;
    }
    x += GAP;
  }
  // Centraliza a fileira.
  for (const o of [...lineup.map((l) => l.body.root), ...dolls.map((d) => d.rig.root)]) o.position.x -= x / 2;
  const tallest = Math.max(1, ...types.map((t) => creatureLook(t)?.height ?? 1));
  const distance = Math.max(4, x * 0.9, tallest * 2.6, deepest * 1.3);
  const side = options.view === "lado";
  // Olham para −z: de frente, a câmera em −z; de costas, em +z; de lado, à direita (+x); na diagonal, entre a frente e o lado.
  if (options.view === "diagonal") camera.position.set(distance * 0.75, tallest * 0.65, -distance * 0.75);
  else camera.position.set(side ? distance : 0, tallest * 0.65, side ? 0 : options.view === "costas" ? distance : -distance);
  camera.lookAt(new Vector3(0, tallest * 0.5, 0));
  const timer = new FrameTimer();
  const hud = new StatsHud(document.body);
  // F52: a contagem do quadro e o pós-processamento (`&pos=`; padrão, o Médio).
  const counter = new FrameStats(renderer.info);
  const unwatch = counter.watch(scene);
  const post = new PostFx(renderer);
  post.bind(atmosphere);
  const resize = () => {
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
  };
  window.addEventListener("resize", resize);
  resize();

  let elapsed = 0;
  let last = performance.now();
  const times = new Map<SkinnedCreature, number>();
  renderer.setAnimationLoop((now: number) => {
    counter.begin();
    const dt = Math.min(0.1, (now - last) / 1000);
    timer.push(now - last);
    last = now;
    elapsed += dt;
    for (const l of lineup) {
      const duration = (CREATURE_MODELS[l.type as keyof typeof CREATURE_MODELS].clips as Readonly<Record<string, { duration: number }>>)[l.clip]!.duration;
      const time = Math.min(l.time ?? options.time ?? elapsed % duration, duration - 1e-3);
      times.set(l.body, time);
      l.body.apply({ samples: [{ clip: l.clip, time, weight: 1 }], lift: 0, roll: 0, pitch: 0, dissolve: 0, clip: l.clip });
    }
    const walking = options.clip === "andando" || options.clip === "correndo";
    for (const d of dolls) applyCreaturePose(d.rig, d.animator.pose({ state: options.clip.startsWith("golpe") ? "golpeando" : "perseguindo", speed: walking ? 3 : 0, stateTime: elapsed, flying: false }, options.time === undefined ? dt : 0, d.rig.legLength));
    applyAtmosphere(atmosphere, sample, options.time === undefined ? elapsed : 0);
    post.render(scene, camera, dt, counter);
    const f = counter.end();
    hud.update(now, () => [`Eldrun · criaturas animadas · ${timer.fps.toFixed(0)} fps · draw calls ${f.drawCalls} (+${f.postCalls} do pós) · programas ${countPrograms(renderer.info.programs).scene}`, `${options.clip}${options.time === undefined ? "" : ` @ ${options.time.toFixed(2)} s`}`]);
  });

  return {
    stats: () => ({
      fps: timer.fps,
      drawCalls: counter.last.drawCalls,
      frameCalls: counter.last.frameCalls,
      // Programas da cena (F52: os do pós à parte, em `post.programs`).
      programs: countPrograms(renderer.info.programs).scene,
      post: post.stats(counter.last),
      skins: skins.stats(),
      models: lineup.map((l) => {
        let mesh: SkinnedMesh | undefined;
        l.body.root.traverse((o) => (o as SkinnedMesh).isSkinnedMesh && o.name === l.variant && (mesh = o as SkinnedMesh));
        const box = mesh ? new Box3().setFromObject(mesh, true) : new Box3();
        return { type: l.type, variant: l.variant, clip: l.clip, time: times.get(l.body) ?? 0, height: +(box.max.y - box.min.y).toFixed(3), minY: +box.min.y.toFixed(3), tris: (mesh?.geometry.index?.count ?? 0) / 3 };
      }),
    }),
    stop() {
      renderer.setAnimationLoop(null);
      post.dispose();
      unwatch();
      window.removeEventListener("resize", resize);
      for (const l of lineup) l.body.dispose();
      skins.dispose();
      hud.remove();
    },
  };
}
